import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { loadCategoryToShort, getLoadCaseLabel, naturalSortComparator } from '@editorUtils'
import { filter, isEqual, reject, uniq } from 'lodash-es'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import {
  DataGridPremium,
  GridColDef,
  GridFilterInitialState,
  GridLogicOperator,
  GridRenderCellParams,
  GridState,
} from '@mui/x-data-grid-premium'
import { useControlStore } from '@editorStores'
import { useElementLabel } from '@editorHooks'
import LoadCustomToolbar from '../LoadsToolBar/CustomToolbar'
import { areaLoadForceFormatter, areaLoadForceValueGetter } from './utils'

interface Props {
  areaLoads: AreaLoad[]
  deadLoadFilterByDefault?: boolean
  setHiddenLoads: (loads: string[]) => void
  hiddenLoads: string[]
  selectedLoad: string | null
  onSelectLoad: (load: string | null) => void
}

const AreaLoadTable = ({
  areaLoads,
  deadLoadFilterByDefault,
  hiddenLoads,
  setHiddenLoads,
  selectedLoad,
  onSelectLoad,
}: Props): ReactElement => {
  const isDrawerExpanded = useControlStore(state => state.isDrawerExpanded)
  const getLabel = useElementLabel()

  const areaLoadGuids = useMemo(() => areaLoads.map(load => load.guid), [areaLoads])

  const defaultFilteredLoads = useMemo(() => {
    if (!deadLoadFilterByDefault) return []

    return reject(areaLoads, load => load.load_case.category === 'Dead').map(load => load.guid)
  }, [deadLoadFilterByDefault, areaLoads])

  const [localHiddenLoads, setLocalHiddenLoads] = useState<string[]>([])
  const [filteredLoads, setFilteredLoads] = useState<string[]>(defaultFilteredLoads)

  const allLoadsAreHidden = useMemo(() => {
    return areaLoadGuids.every(guid => hiddenLoads.includes(guid))
  }, [areaLoadGuids, hiddenLoads])

  const setAllLoadsVisible = useCallback(() => {
    // only set the given areaLoads to visible
    setLocalHiddenLoads(reject(localHiddenLoads, guid => areaLoadGuids.includes(guid)))
  }, [localHiddenLoads, areaLoadGuids])

  const setAllLoadsHidden = useCallback(() => {
    // Combine existing hidden loads with the area load guids, removing duplicates
    setLocalHiddenLoads([...new Set([...localHiddenLoads, ...areaLoadGuids])])
  }, [localHiddenLoads, areaLoadGuids])

  const handleVisibilityColumnHeaderClick = useCallback(() => {
    if (allLoadsAreHidden) {
      setAllLoadsVisible()
    } else {
      setAllLoadsHidden()
    }
  }, [allLoadsAreHidden, setAllLoadsHidden, setAllLoadsVisible])

  const toggleHideLoad = useCallback(
    (loadGuid: string) => {
      if (localHiddenLoads.includes(loadGuid)) {
        setLocalHiddenLoads(reject(localHiddenLoads, hiddenLoad => hiddenLoad === loadGuid))
      } else {
        setLocalHiddenLoads([...localHiddenLoads, loadGuid])
      }
    },
    [localHiddenLoads],
  )

  useEffect(() => {
    const allHiddenLoads = uniq([...localHiddenLoads, ...filteredLoads])
    setHiddenLoads(allHiddenLoads)

    return () => setHiddenLoads([])
  }, [filteredLoads, localHiddenLoads, setHiddenLoads])

  const rows = useMemo(() => {
    return areaLoads.map(load => {
      const source = load.user_shown_source
      return {
        id: load.guid,
        ...load,
        sourceGuid: source,
        sourceLabel: source ? getLabel(source) : '',
        visible: !hiddenLoads.includes(load.guid),
        // fields for filtering
        loadCaseType: load.load_case.load_case_type,
        loadCaseCategory: load.load_case.category,
        loadCaseTypeLabelShort: getLoadCaseLabel(load.load_case)?.short,
        loadCaseTypeLabel: getLoadCaseLabel(load.load_case)?.label,
        loadCaseCategoryShort: loadCategoryToShort[load.load_case.category],
      }
    })
  }, [areaLoads, hiddenLoads, getLabel])

  const rowSelectionModel = useMemo(() => {
    if (!selectedLoad) return []

    const selectedRow = rows.find(row => row.guid === selectedLoad)
    return selectedRow ? [selectedRow.guid] : []
  }, [selectedLoad, rows])

  const defaultColumnProps = {
    renderHeaderFilter: () => null,
  }

  const columnWidths = isDrawerExpanded
    ? {
        visibility: 30,
        force: 110,
        loadCategoryType: 80,
        loadCaseType: 110,
        source: 80,
      }
    : {
        visibility: 30,
        force: 75,
        loadCategoryType: 35,
        loadCaseType: 50,
        source: 60,
      }

  const columns: GridColDef[] = [
    {
      ...defaultColumnProps,
      field: 'visible',
      headerName: 'Sichtbarkeit',
      width: columnWidths.visibility,
      minWidth: columnWidths.visibility,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      disableColumnMenu: true,
      type: 'boolean',
      renderHeader: () => {
        return (
          <IconButton onClick={handleVisibilityColumnHeaderClick} size="small">
            {allLoadsAreHidden ? (
              <VisibilityOff fontSize="inherit" />
            ) : (
              <Visibility fontSize="inherit" />
            )}
          </IconButton>
        )
      },
      renderCell: (params: GridRenderCellParams) => (
        <IconButton onClick={() => toggleHideLoad(params.row.guid)} size="small">
          {params.row.visible ? (
            <Visibility fontSize="inherit" />
          ) : (
            <VisibilityOff fontSize="inherit" />
          )}
        </IconButton>
      ),
    },
    {
      ...defaultColumnProps,
      field: 'loadCaseType',
      headerName: 'Fall',
      width: columnWidths.loadCaseType,
      minWidth: columnWidths.loadCaseType,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      valueGetter: (_, row: AreaLoad) => getLoadCaseLabel(row.load_case)?.short,
      sortComparator: naturalSortComparator,
    },
    {
      ...defaultColumnProps,
      field: 'loadCaseTypeLabelShort',
      headerName: 'FallKurzbez.',
      width: columnWidths.loadCaseType,
      minWidth: columnWidths.loadCaseType,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      hideable: true,
    },
    {
      field: 'force',
      headerName: 'q',
      width: columnWidths.force,
      sortable: true,
      headerAlign: 'center',
      align: 'right',
      renderHeader: () => {
        return (
          <Stack direction="column" spacing={0} padding={0} alignItems="center">
            <Typography>q</Typography>
            <Typography>[kN/m²]</Typography>
          </Stack>
        )
      },
      valueGetter: (_, row: AreaLoad) => areaLoadForceValueGetter(row.force),
      valueFormatter: areaLoadForceFormatter,
      type: 'number',
    },
    {
      field: 'loadCaseCategoryShort',
      headerName: 'Kat.',
      width: columnWidths.loadCategoryType,
      minWidth: columnWidths.loadCategoryType,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
    },
  ] as GridColDef[]

  const isDrawerExpandedExtraProps = isDrawerExpanded
    ? {
        headerFilters: true,
        headerFilterHeight: 75,
      }
    : {}

  const slots = isDrawerExpanded
    ? {
        toolbar: LoadCustomToolbar,
      }
    : {
        toolbar: LoadCustomToolbar,
      }

  const gridFilterInitialState: GridFilterInitialState | undefined = deadLoadFilterByDefault
    ? {
        filterModel: {
          items: [
            {
              field: 'loadCaseType',
              operator: 'equals',
              value: 'g',
            },
            {
              field: 'force',
              operator: '<',
            },
          ],
          quickFilterExcludeHiddenColumns: false,
          quickFilterLogicOperator: GridLogicOperator.Or,
        },
      }
    : {
        filterModel: {
          items: [{ field: 'force', operator: '<' }],
          quickFilterExcludeHiddenColumns: false,
          quickFilterLogicOperator: GridLogicOperator.Or,
        },
      }

  const handleStateChange = useCallback(
    (state: GridState) => {
      const filteredRows = filter(
        Object.entries(state.filter.filteredRowsLookup),
        ([, visible]) => !visible,
      )

      const filteredKeys = filteredRows.map(visibleRow => visibleRow[0])

      if (!isEqual(filteredKeys, filteredLoads)) {
        setFilteredLoads(filteredKeys)
      }
    },
    [filteredLoads],
  )

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <DataGridPremium
        rows={rows}
        columns={columns}
        columnHeaderHeight={75}
        density="compact"
        hideFooter
        onRowClick={params => {
          onSelectLoad(params.row.guid)
        }}
        rowSelectionModel={rowSelectionModel}
        initialState={{
          sorting: {
            sortModel: [{ field: 'loadCaseType', sort: 'asc' }],
          },
          pagination: {
            paginationModel: { pageSize: 25 },
          },
          columns: {
            columnVisibilityModel: {
              loadCaseTypeLabelShort: false,
            },
          },
          filter: gridFilterInitialState,
        }}
        autoPageSize
        getRowId={row => row.id}
        disableRowSelectionOnClick
        disableRowGrouping
        // @ts-expect-error
        slots={slots}
        slotProps={{
          toolbar: {
            // @ts-expect-error
            loadsCombined: false, // Area loads don't support combining yet
            onLoadsCombinedChange: undefined,
          },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            padding: 0,
          },
          '& .MuiDataGrid-columnHeader': {
            padding: 0,
          },
        }}
        onStateChange={handleStateChange}
        {...isDrawerExpandedExtraProps}
        data-cy="area-load-table"
      />
    </Box>
  )
}

export default AreaLoadTable
